import { isEqual } from 'lodash-es';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Button, Icon, formatNumber } from '@column/column-ui-kit';

import { TransferFiltersModal } from '../Transfers/_components/TransferFiltersModal';
import {
  PageTransferExportModalContent,
  ResetFilter,
  TransferLocationState,
  transferTableColumns,
} from '../Transfers/Overview';
import { normalizeTransferTypes } from '~/app/pages/Transfers/_utils/filterUtils';
import { ROUTE } from '~/app/routes';
import {
  PopoverFilterAmount,
  PopoverFilterAmountData,
  PopoverFilterTransferDate,
  PopoverFilterTransferDateData,
  PopoverFilterTransferStatus,
  PopoverFilterTransferType,
  PopoverFilterTransferTypeData,
  PopoverFilterWrapper,
} from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { OverviewTable, OverviewTableRefProps } from '~/components/OverviewTable';
import { Search } from '~/components/Search';
import { useQueryParams } from '~/hooks';
import { TransferFilterType, useCheckTransferId } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { TransferListResponse, TransferRepository, transfers } from '~/repositories';
import { TransferTypeOptions } from '~/repositories/Transfer/TransferRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { downloadBlob, getDateFormat } from '~/util';

import { BankAccountSectionProps } from './Section';

const ExportWrapper = styled.div`
  display: flex;
  align-items: end;
  flex-direction: column;
  width: 100%;
`;

const MobileFiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const FilterActions = styled.div<{ $isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${({ $isHidden }) => ($isHidden ? 0 : '0 8px')};
  transition: opacity 0.2s;
  width: ${({ $isHidden }) => ($isHidden ? 0 : 'auto')};
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

export const PageBankAccountsTransfers: React.FC = () => {
  const { openAlert, closeAlert, getAlertData, setAlertData } = useDialog();
  const { bankAccount } = useOutletContext<BankAccountSectionProps>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addDangerNotification } = useNotificationStore();
  const { settings, updateSettings } = useSessionStore();
  const overviewTableRef = useRef<OverviewTableRefProps>(null);
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>('relative');
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const locationState = location.state as TransferLocationState;

  const mountedRef = useRef<boolean>(false);

  const { queryParams, getQueryObject } = useQueryParams<TransferFilterType>();

  const [totalResultsCount, setTotalResultsCount] = useState<number>(0);

  const handleResponse = useCallback((response?: TransferListResponse) => {
    setTotalResultsCount(response?.totalResultsCount ?? 0);
  }, []);

  const [filterByIdTooltipContent, setFilterByIdTooltipContent] = useState<string>('');
  const [filterByIdValue, setFilterByIdValue] = useState<string>('');

  const timeFormat = settings.tableSettings?.transfers?.timeFormat || 'relative';

  useEffect(() => {
    setSelectedTimeOption(timeFormat);
  }, [timeFormat]);

  const handleTimeOptionChange = useCallback(
    async (value: string) => {
      setSelectedTimeOption(value);
      try {
        await updateSettings({
          tableSettings: {
            transfers: {
              limit: settings.tableSettings?.transfers?.limit || 10,
              timeFormat: value === 'relative' ? TableTimeFormat.Relative : TableTimeFormat.Absolute,
            },
          },
        });
      } catch (error) {
        addDangerNotification({ content: 'Failed to save time format preference' });
      }
    },
    [updateSettings, settings.tableSettings]
  );

  const [isSmallViewport, setIsSmallViewport] = useState<boolean>(false);

  useEffect(() => {
    if (!queryParams) {
      return undefined;
    }

    const { counterpartyId, accountNumberId } = queryParams;

    setFilterByIdValue(counterpartyId || accountNumberId || '');
  }, []);

  const handleFilterByIdValueChange = useCallback((value: string) => {
    setFilterByIdValue(value);
    setFilterByIdTooltipContent('');
  }, []);

  const [amountFilterData, setAmountFilterData] = useState<PopoverFilterAmountData | undefined>(() => {
    if (!queryParams) return {};
    const { amount, amountGte, amountLte } = queryParams;
    return {
      amount: amount ? Number(amount) : undefined,
      amountGte: amountGte ? Number(amountGte) : undefined,
      amountLte: amountLte ? Number(amountLte) : undefined,
    };
  });
  const isAmountFilterActive = useMemo(() => {
    if (
      (amountFilterData?.amount && amountFilterData?.amount > 0) ||
      (amountFilterData?.amountGte && amountFilterData?.amountGte > 0) ||
      (amountFilterData?.amountLte && amountFilterData?.amountLte > 0)
    ) {
      return true;
    }
    return false;
  }, [amountFilterData]);

  const [transferTypeFilterData, setTransferTypeFilterData] = useState<PopoverFilterTransferTypeData>(() => {
    if (!queryParams) return {};
    const { isIncoming, type } = queryParams;
    return {
      isIncoming: typeof isIncoming !== 'undefined' ? String(isIncoming) === 'true' : undefined,
      type: type ? (type.split(',') as any) : [],
    };
  });
  const isTransferTypeFilterActive = useMemo(() => {
    if (
      (transferTypeFilterData && transferTypeFilterData.type && transferTypeFilterData.type.length > 0) ||
      (transferTypeFilterData && typeof transferTypeFilterData.isIncoming !== 'undefined')
    ) {
      return true;
    }
    return false;
  }, [transferTypeFilterData]);

  const [transferStatusFilterData, setTransferStatusFilterData] = useState<string[] | undefined>(() => {
    if (!queryParams) return [];
    const { status } = queryParams;
    return status ? status.split(',') : [];
  });

  const [transferDateFilterData, setTransferDateFilterData] = useState<Partial<PopoverFilterTransferDateData>>(() => {
    if (!queryParams) return {};
    const { createdAtGte, createdAtLt } = queryParams;
    return {
      createdAtGte: createdAtGte && createdAtGte.length > 0 ? createdAtGte : undefined,
      createdAtLt: createdAtLt && createdAtLt.length > 0 ? createdAtLt : undefined,
    };
  });
  const isDateFilterActive = useMemo(() => {
    if (transferDateFilterData?.createdAtGte && transferDateFilterData?.createdAtLt) {
      return true;
    }
    return false;
  }, [transferDateFilterData]);

  const isFilterActive = useMemo(() => {
    return (
      filterByIdValue ||
      isAmountFilterActive ||
      isTransferTypeFilterActive ||
      (transferStatusFilterData && transferStatusFilterData.length > 0) ||
      isDateFilterActive
    );
  }, [filterByIdValue, isAmountFilterActive, transferTypeFilterData, transferStatusFilterData, isDateFilterActive]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const threshold = isFilterActive ? 1200 : 1000;
      setIsSmallViewport(window.innerWidth <= threshold);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFilterActive]);

  const checkTransferId = useCheckTransferId({
    onSuccess: (id) => {
      const url = transfers.find((transfer) => transfer.idString && id.startsWith(transfer.idString))?.id;

      navigate(`${ROUTE.TRANSFERS}/edit/${url}/${id}`);
    },
    onError: (message) => {
      setFilterByIdTooltipContent(message);
    },
  });

  const handleFilterById = useCallback(
    (value: string) => {
      if (value.length > 0 && transfers.some((transfer) => transfer.idString && value.startsWith(transfer.idString))) {
        checkTransferId(value);

        return;
      }

      if (value.startsWith('cpty_')) {
        overviewTableRef.current?.setQueryParams({
          counterpartyId: value,
          accountNumberId: undefined,
        });

        return;
      }

      if (value.startsWith('acno_')) {
        overviewTableRef.current?.setQueryParams({
          accountNumberId: value,
          counterpartyId: undefined,
        });

        return;
      }

      if (value && value.length > 0) {
        setFilterByIdTooltipContent('Not a supported ID. Enter a Bank Account ID, Counterparty ID, or Transfer ID.');
      } else {
        setTimeout(() => overviewTableRef.current?.reset(), 0);
        setFilterByIdTooltipContent('');
      }
    },
    [overviewTableRef]
  );

  useLayoutEffect(() => {
    if (locationState?.trigger === 'delayedFetch' && overviewTableRef.current) {
      window.history.replaceState({}, '');

      setTimeout(() => {
        overviewTableRef.current?.fetch();

        setTimeout(() => {
          overviewTableRef.current?.fetch();
        }, 1000);
      }, 500);
    }
  }, [locationState?.trigger, overviewTableRef]);

  const [fetchFilters, setFetchFilters] = useState<TransferFilterType | null>(null);

  useEffect(() => {
    const paginationFields = ['page', 'startingAfter', 'endingBefore', 'limit', 'bankAccountId'];
    const filterParams = queryParams
      ? Object.fromEntries(Object.entries(queryParams).filter(([key]) => !paginationFields.includes(key)))
      : {};

    const entries = Object.fromEntries(
      Object.entries({
        ...filterParams,
        amount: amountFilterData?.amount && amountFilterData.amount > 0 ? amountFilterData?.amount : undefined,
        amountGte:
          amountFilterData?.amountGte && amountFilterData.amountGte > 0 ? amountFilterData?.amountGte : undefined,
        amountLte:
          amountFilterData?.amountLte && amountFilterData.amountLte > 0 ? amountFilterData?.amountLte : undefined,
        isIncoming: transferTypeFilterData.isIncoming,
        ...transferDateFilterData,
        type:
          transferTypeFilterData.type && transferTypeFilterData.type?.length > 0
            ? transferTypeFilterData.type?.join(',')
            : undefined,
        status:
          transferStatusFilterData && transferStatusFilterData?.length > 0
            ? transferStatusFilterData?.join(',')
            : undefined,
      }).filter(([, value]) => {
        if (value === undefined || value === null) {
          return false;
        }

        if (typeof value === 'object' && Object.keys(value).length === 0) {
          return false;
        }

        if (Array.isArray(value) && value.length < 1) {
          return false;
        }

        return true;
      })
    );

    setFetchFilters((s) => (isEqual(entries, s) ? s : entries));
  }, [queryParams, amountFilterData, transferTypeFilterData, transferStatusFilterData, transferDateFilterData]);

  const handleFilterReset = useCallback(() => {
    setAmountFilterData({});
    setTransferTypeFilterData({});
    setTransferStatusFilterData([]);
    setTransferDateFilterData({});

    setFilterByIdValue('');
    setFilterByIdTooltipContent('');

    setFetchFilters(null);

    setTimeout(() => overviewTableRef.current?.setQueryParams({}), 0);
  }, []);

  const [exportAnimate, setExportAnimate] = useState<boolean>(false);

  const handleExport = () => {
    if (getAlertData('export-transfers')?.isLoading) {
      return;
    }

    setAlertData('export-transfers', { isLoading: true });

    setExportAnimate(true);

    const params = { ...getQueryObject };

    if (params?.page) {
      delete params.page;
    }

    if (params?.limit) {
      delete params.limit;
    }

    if (params?.startingAfter) {
      delete params.startingAfter;
    }

    if (params?.endingBefore) {
      delete params.endingBefore;
    }

    TransferRepository.export({ ...params, bankAccountId: bankAccount.id })
      .then((response) => {
        downloadBlob(
          response,
          `${
            String(getAlertData('export-transfers')?.customFileName).length > 0
              ? getAlertData('export-transfers')?.customFileName
              : `transfers-${getDateFormat(new Date())}`
          }.csv`
        );
      })
      .then(() => {
        closeAlert();
      })
      .catch((e) =>
        addDangerNotification({
          content: e.message,
          display: 'popup',
        })
      )
      .finally(() => {
        setAlertData('export-transfers', { isLoading: false });
      });
  };

  const handleExportClick = () => {
    let text = `Do you want to export & download the .csv file for ${formatNumber(totalResultsCount, false)} transfers?`;

    if (!isFilterActive) {
      text = `Are you sure you want to export & download the .csv file for all transfers (${formatNumber(
        totalResultsCount,
        false
      )}) without any filters applied?`;
    }

    if (totalResultsCount > 1000) {
      text = `Are you sure to export & download the .csv file for ${formatNumber(
        totalResultsCount,
        false
      )} transfers? This may take a while.`;
    }

    openAlert({
      headline: 'Export',
      content: <PageTransferExportModalContent text={text} />,
      confirmButton: {
        label: 'Download',
        variant: 'primary',
      },
      onSuccess: handleExport,
      type: 'primary',
      icon: 'AnimationDownload',
    });
  };

  useEffect(() => {
    setTimeout(() => {
      mountedRef.current = true;
    }, 2000);
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        () => {
          if (!mountedRef.current) {
            return;
          }

          handleFilterReset();
        },
        {
          equalityFn: shallow,
        }
      ),
    []
  );

  return (
    <>
      <TransferFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        hideAccount
        initialAmount={amountFilterData}
        initialType={{
          isIncoming: transferTypeFilterData.isIncoming,
          type: transferTypeFilterData.type as unknown as string[],
        }}
        initialStatus={transferStatusFilterData}
        initialDate={{
          createdAtGte: transferDateFilterData?.createdAtGte,
          createdAtLt: transferDateFilterData?.createdAtLt,
        }}
        initialFilterByIdValue={filterByIdValue}
        onFilterById={handleFilterById}
        onApply={(values) => {
          setAmountFilterData({ amount: values.amount, amountGte: values.amountGte, amountLte: values.amountLte });
          setTransferTypeFilterData({
            isIncoming: values.isIncoming,
            type: normalizeTransferTypes(
              values.type as unknown[] | undefined
            ) as unknown as (typeof TransferTypeOptions)[],
          });
          setTransferStatusFilterData(values.status);
          setTransferDateFilterData({ createdAtGte: values.createdAtGte, createdAtLt: values.createdAtLt });
        }}
      />

      <OverviewTable
        tableId="transfers"
        ref={overviewTableRef}
        fetchFilter={fetchFilters}
        persistParams={{ bankAccountId: bankAccount.id }}
        columns={transferTableColumns(selectedTimeOption, handleTimeOptionChange)}
        onResponse={handleResponse}
        onLimitChange={() => handleFilterReset()}
        action={
          <>
            {isSmallViewport ? (
              <MobileFiltersWrapper>
                <Button size="small" variant="secondary" onClick={() => setIsFiltersOpen(true)}>
                  <Icon.Filter />
                  Filters
                </Button>

                <FilterActions $isHidden={!isFilterActive}>
                  <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                    Reset
                  </ResetFilter>
                </FilterActions>
              </MobileFiltersWrapper>
            ) : (
              <>
                <PopoverFilterWrapper
                  label="Amount"
                  onSubmit={(data) => {
                    setAmountFilterData(data);
                    if (!data.amount && !data.amountGte && !data.amountLte) {
                      setTimeout(() => {
                        overviewTableRef.current?.setQueryParams({
                          amount: undefined,
                          amountGte: undefined,
                          amountLte: undefined,
                        });
                      }, 0);
                    }
                  }}
                  isActive={isAmountFilterActive}
                >
                  <PopoverFilterAmount data={amountFilterData ?? {}} />
                </PopoverFilterWrapper>
                <PopoverFilterWrapper
                  label="Type"
                  onSubmit={(data) => {
                    setTransferTypeFilterData(data);
                    if ((!data.type || data.type.length === 0) && typeof data.isIncoming === 'undefined') {
                      setTimeout(() => {
                        overviewTableRef.current?.setQueryParams({
                          type: undefined,
                          isIncoming: undefined,
                        });
                      }, 0);
                    }
                  }}
                  isActive={isTransferTypeFilterActive}
                >
                  <PopoverFilterTransferType data={transferTypeFilterData ?? {}} />
                </PopoverFilterWrapper>
                <PopoverFilterWrapper
                  label="Status"
                  onSubmit={(data) => {
                    setTransferStatusFilterData(data);
                    if (!data || data.length === 0) {
                      setTimeout(() => {
                        overviewTableRef.current?.setQueryParams({
                          status: undefined,
                        });
                      }, 0);
                    }
                  }}
                  isActive={transferStatusFilterData && transferStatusFilterData.length > 0}
                >
                  <PopoverFilterTransferStatus data={transferStatusFilterData ?? []} />
                </PopoverFilterWrapper>
                <PopoverFilterWrapper
                  label="Date"
                  onSubmit={(data) => {
                    setTransferDateFilterData(data);
                    if (!data.createdAtGte && !data.createdAtLt) {
                      setTimeout(() => {
                        overviewTableRef.current?.setQueryParams({
                          createdAtGte: undefined,
                          createdAtLt: undefined,
                        });
                      }, 0);
                    }
                  }}
                  isActive={isDateFilterActive}
                >
                  <PopoverFilterTransferDate data={transferDateFilterData ?? {}} />
                </PopoverFilterWrapper>

                <Search
                  value={filterByIdValue}
                  onFilterSubmit={handleFilterById}
                  placeholder="Filter by ID"
                  tooltipContent={filterByIdTooltipContent}
                  onValueChange={handleFilterByIdValueChange}
                />

                {isFilterActive && (
                  <ResetFilter
                    size="small"
                    variant="subtle-primary"
                    icon={<Icon.Reset />}
                    iconRight
                    onClick={handleFilterReset}
                  >
                    Reset filters
                  </ResetFilter>
                )}
              </>
            )}

            <ExportWrapper>
              <Button
                icon={<Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />}
                size="small"
                variant="secondary"
                onClick={handleExportClick}
              >
                Export
              </Button>
            </ExportWrapper>
          </>
        }
        rowClick={(row) => {
          navigate(
            `${ROUTE.TRANSFERS}/edit/${(row.original.type || 'ach')
              .toString()
              .toLowerCase()
              .replace('debit', '')
              .replace('credit', '')
              .replace('deposit', '')
              .replace('swift', 'intl-wire')
              .replace('. ', '-')
              .replace('_', '')
              .trim()}/${row.original.id}`
          );
        }}
      />
    </>
  );
};
